import _get from 'lodash/get';
import { LAYOUT_ENTITIES, LAYOUT_FRONTSPACES } from '@bedrock/fido-sdk';
import { RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import {
  blockTemplateTypes,
  selectorTemplateTypes,
  templateTypes,
} from '../../modules/tornado/templates/templateTypes';
import { PAGE_STATE, PLACEHOLDER } from '../../modules/tornado/utils/virtualization';
import { shouldDisplayProfilesGate } from '../lock/lock.selectors';
import { titleFilter } from './platform/default/layoutFilter';
import { getScreenSaverImageOptions, getContentImageOptions, prepareItemImages } from './layout.image.utils';

export * from './platform/default/layoutFilter';

export const isScreensaverEntity = (entityType, entityId) =>
  entityType === LAYOUT_ENTITIES.frontspace && entityId === LAYOUT_FRONTSPACES.tvapppreview;

export const isSearchEntity = (entityType, entityId) =>
  entityType === LAYOUT_ENTITIES.frontspace && entityId === LAYOUT_FRONTSPACES.search;

export const isOffersEntity = (entityType, entityId) =>
  entityType === LAYOUT_ENTITIES.frontspace && entityId === LAYOUT_FRONTSPACES.offers;

export const isProfilesGateEntity = (entityType, entityId) =>
  entityType === LAYOUT_ENTITIES.frontspace && entityId === LAYOUT_FRONTSPACES.profilesgate;

export const shouldDisplayLanding = data =>
  _get(data, 'entity.id') === LAYOUT_FRONTSPACES.landing && _get(data, 'entity.type') === LAYOUT_ENTITIES.frontspace;

export const shouldDisplaySubscriptionExpiration = data => {
  const { id: entityId, type: entityType } = data.entity;
  const isEmptyLayout = !_get(data, 'blocks.length');

  return isOffersEntity(entityType, entityId) && isEmptyLayout;
};

export const isProfilesGates = data => {
  const { id: entityId, type: entityType } = data.entity;

  return isProfilesGateEntity(entityType, entityId);
};

export const shouldAutoSelectProfile = (data, state) => {
  const isProfilesGatesSelectedByUser = shouldDisplayProfilesGate(state);

  if (isProfilesGatesSelectedByUser) {
    return false;
  }

  return _get(data, 'blocks[0].content.items.length') === 1;
};

export const hasAlternativeContent = block => Boolean(block.alternativeContent);

export const isTemplateIncluded = (template, collection) => collection.includes(template);

export const isKnownSelectorTemplateType = block =>
  Object.keys(selectorTemplateTypes).includes(block.alternativeContent.selectorTemplateId);

export const isKnownBlockTemplateType = block => Object.keys(blockTemplateTypes).includes(block.blockTemplateId);

export const isKnownTemplateType = block => Object.keys(templateTypes).includes(block.content.contentTemplateId);

export const prepareItem = item => {
  if (!item) {
    return item;
  }

  const { itemContent, ...rest } = item;
  // In case if we are parsing pre-parsed items (i.e. Algolia hits), which don't have the "itemContent" field
  const container = itemContent || item;

  return {
    ...rest,
    ...itemContent,
    title: titleFilter(container.title),
    extraTitle: titleFilter(container.extraTitle),
  };
};

export const getFormattedItems = (items, imageOptions, forceGhostServicePicto = false) =>
  items.map(item => prepareItemImages(prepareItem(item), imageOptions, forceGhostServicePicto));

const GHOSTY_TEMPLATES = ['Card', 'Jacket', 'Portrait', 'Poster', 'Totem', 'HorizontalCard'];

/**
 * Creates a set of content blocks adding fields required to properly display content
 *
 * @param {object} entity Layout API entity
 * @param {string} entity.entityId Layout API entity ID
 * @param {string} entity.entityType Layout API entity type
 * @param {('FULL_HD'|'HD')} screenSize Current screen size
 * @param {Object} contentTemplateOptions a set of template-defined options that allows to define content size and image options
 *
 * @returns {Object}
 */
export const prepareBlock = (entity, screenSize, contentTemplateOptions) => {
  const { entityId, entityType } = entity || {};
  const renderContext = isSearchEntity(entityType, entityId) ? RENDERING_CONTEXT.SEARCH : RENDERING_CONTEXT.GENERAL;

  return ({ id, content, ...block }) => {
    const items = content ? content.items : [];
    const imageOptions = isScreensaverEntity(entityType, entityId)
      ? getScreenSaverImageOptions(screenSize)
      : getContentImageOptions(content.contentTemplateId, contentTemplateOptions, screenSize, renderContext);

    const templateId = block ? block.templateId : '';
    const forceGhostServiceIcon = GHOSTY_TEMPLATES.some(templatePrefix => templateId.startsWith(templatePrefix));
    const { title } = block;

    return {
      ...block,
      title: title
        ? {
            long: titleFilter(title.long),
            short: titleFilter(title.short),
          }
        : title,
      id,
      content: {
        ...content,
        items: getFormattedItems(items, imageOptions, forceGhostServiceIcon),
      },
    };
  };
};

export const prepareAlternateContent = block => {
  const concurrentBlocks = _get(block, 'alternativeContent.concurrentBlocks');

  if (concurrentBlocks && concurrentBlocks.length) {
    const { alternativeContent } = block;

    // Original block already contains a proper data for the first concurrent block, referencing to the origin
    concurrentBlocks[0].id = block.id;
    concurrentBlocks[0].content = block.content;

    const result = alternativeContent.concurrentBlocks.map(({ content, theme, ...rest }) => ({
      ...rest,
      theme: theme || block.theme,
      content: {
        ...content,
        pagination: content.items ? content.pagination : { ...content.pagination, nextPage: 1 },
        items: content.items || [],
      },
    }));

    return {
      ...block,
      alternativeContent: {
        ...block.alternativeContent,
        concurrentBlocks: result,
      },
    };
  }

  return block;
};

export const prepareLastViewedProgress = (blocks, clipId, clipProgress) => {
  for (let i = 0; i < blocks.length; i += 1) {
    const {
      content: { items = [] },
    } = blocks[i];

    for (let j = 0; j < items.length; j += 1) {
      const itemId = _get(items[j], 'action.target.value_layout.id');
      if (clipId === itemId && items[j].progress === 0) {
        blocks[i].content.items[j].progress = clipProgress;

        return;
      }
    }
  }
};

export const PLAYABLE_ENTITIES = [
  LAYOUT_ENTITIES.audio,
  LAYOUT_ENTITIES.video,
  LAYOUT_ENTITIES.live,
  LAYOUT_ENTITIES.radio,
];

export const isPlayableEntity = entityType => PLAYABLE_ENTITIES.includes(entityType);

export const getLayoutEntityAsString = layout => {
  if (!layout || !layout.entity) {
    return '';
  }

  const { type, id } = layout.entity;

  return `${type}_${id}`;
};

export const isValidItemPosition = (blockIndex, itemIndex) =>
  Number.isFinite(blockIndex) && Number.isFinite(itemIndex) && blockIndex >= 0 && itemIndex >= 0;

export { constructImageOptions } from './layout.image.utils';

export const pagesToLoad = (totalItems, itemsPerPage, items = []) => {
  const result = {};

  if (!totalItems || !itemsPerPage) {
    return result;
  }

  for (let i = 0; i < totalItems; i += itemsPerPage) {
    const start = i;
    const end = Math.max(Math.min(start + itemsPerPage, totalItems) - 1, 0);
    const page = i / itemsPerPage;
    result[page] =
      Boolean(items[start] && items[end]) && items[start] !== PLACEHOLDER && items[end] !== PLACEHOLDER
        ? PAGE_STATE.LOADED
        : PAGE_STATE.MISSING;
  }

  return result;
};

export const hasAlertView = layout => layout?.blocks?.[0]?.templateId === 'AlertView' ?? false;
